import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CrewTravelListResponse } from '../types';
import { CrewChangesCostReport } from './CrewChangesCostReport';
import {
  crewChangesCostBackPath,
  crewChangesCostListApiUrl,
  kindLabel,
  statusLabel,
} from './crewChangesCostPrintMeta';
import type { CrewTravelKind, CrewTravelStatus } from '../types';

const CrewChangesCostReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const listUrl = useMemo(() => crewChangesCostListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CrewTravelListResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const kind = searchParams.get('kind')?.trim() ?? '';
  const status = searchParams.get('status')?.trim() ?? '';
  const q = searchParams.get('q')?.trim() ?? '';

  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselName = useMemo(() => {
    if (!vesselId) return null;
    const match = (lookups?.vessels ?? []).find((v) => String(v.id) === vesselId);
    return match?.vesselName ?? `Vessel #${vesselId}`;
  }, [lookups?.vessels, vesselId]);

  const rows = useMemo(() => {
    const list = [...(data?.rows ?? [])];
    list.sort((a, b) => {
      const ad = a.dateFrom || '';
      const bd = b.dateFrom || '';
      if (ad !== bd) return bd.localeCompare(ad);
      return b.id - a.id;
    });
    return list;
  }, [data?.rows]);

  const backTo = crewChangesCostBackPath(searchParams);

  const filterLabels = useMemo(
    () => ({
      vessel: vesselName,
      kind:
        kind === 'CREW_CHANGE' || kind === 'VISIT'
          ? kindLabel(kind as CrewTravelKind)
          : null,
      status: status
        ? statusLabel(status as CrewTravelStatus)
        : null,
      search: q || null,
    }),
    [kind, q, status, vesselName],
  );

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading Crew Changes Cost…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load crew changes cost.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Crew Changes Cost
        </Link>
      </div>
    );
  }

  const subtitleParts = [
    vesselName || 'All vessels',
    filterLabels.kind,
    filterLabels.status,
    `${rows.length} record${rows.length === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Crew Changes Cost"
      title="Crew Changes Cost"
      subtitle={subtitleParts.join(' · ')}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <CrewChangesCostReport data={data} rows={rows} filterLabels={filterLabels} />
    </ReportView>
  );
};

export default CrewChangesCostReportPage;
